import { isImageSource } from "@/shared/utils/is-image-source";
import { StaticEntityType } from "@config";
import { produce } from "immer";
import { parseHTMLContent } from "./parse-html-content";

export const setSlideDefaultValues = ({
  slide,
  defaultValues,
}: {
  slide: StaticEntityType;
  defaultValues: Record<string, unknown>;
}) => {
  return produce(slide, (draft) => {
    const processElement = (element: StaticEntityType) => {
      const { name } = element.settings;
      const updateFunctions: Record<
        string,
        (
          settings: Record<string, unknown>,
          values: Record<string, unknown>,
        ) => Record<string, unknown>
      > = {
        Image: updateImage,
        Logo: updateImage,
        Video: updateVideo,
        RichText: updateRichText,
        CollapsibleText: updateRichText,
        Grid: updateGrid,
        AnimatedNumbers: updateAnimatedNumbers,
        Button: updateButton,
        Chart: updateChart,
        Icon: updateIcon,
      };

      const updateFunction = updateFunctions[name];
      if (updateFunction) {
        element.settings = updateFunction(element.settings, defaultValues);
      }
    };

    draft.settings = updateSlideBackground(draft.settings, defaultValues);
    draft.children.forEach((combo) =>
      combo.children.forEach((set) => {
        if (combo.settings.name === "Tabs") {
          set.settings = updateSingleTabs(set.settings, defaultValues);
        }
        set.children.forEach((group) => group.children.forEach(processElement));
      }),
    );
  });
};

// ============ Helper Functions ============

const updateImage = (
  settings: Record<string, unknown>,
  json: Record<string, unknown>,
  reset = true,
) => {
  const key = settings.AIClassificationAnchor as string;
  const value = json[key || ""] as string;

  if (
    !value ||
    !isImageSource(value) ||
    value.includes(".mp4") ||
    value.includes(".webm")
  )
    return settings;

  return produce(settings, (draft) => {
    draft.src = value;
    if (reset) delete draft.AIClassificationAnchor;
  });
};

const updateVideo = (
  settings: Record<string, unknown>,
  json: Record<string, unknown>,
  reset = true,
) => {
  const key = settings.AIClassificationAnchor as string;
  const value = json[key || ""] as string;

  if (
    !value ||
    !value.startsWith("https") ||
    (!value.endsWith(".mp4") && !value.endsWith(".webm"))
  )
    return settings;

  return produce(settings, (draft) => {
    draft.src = value;
    if (reset) delete draft.AIClassificationAnchor;
  });
};

const updateRichText = (
  settings: Record<string, unknown>,
  json: Record<string, unknown>,
  reset = true,
) => {
  return produce(settings, (draft) => {
    draft.content = parseHTMLContent(draft.content as string, json, reset);
  });
};

const updateGrid = (
  settings: Record<string, unknown>,
  json: Record<string, unknown>,
  reset = true,
) => {
  const data = settings.data as Record<string, unknown>[] | undefined;
  if (!Array.isArray(data) || data.length === 0) return settings;
  if (typeof data[0] === "string") return settings;

  return produce(settings, (draft) => {
    const draftData = draft.data as Record<string, unknown>[] | undefined;
    if (!Array.isArray(draftData)) return;
    draftData.forEach((item, index) => {
      const classification = item.classification as string;
      const value = json[classification || ""] as string;

      if (value && isImageSource(value)) {
        draftData[index] = {
          ...item,
          value: value,
          ...(!reset && { classification }),
        };

        if (reset) delete draftData[index].classification;
      }
    });
  });
};

const updateAnimatedNumbers = (
  settings: Record<string, unknown>,
  json: Record<string, unknown>,
  reset = true,
) => {
  const classification = settings.aiClassification as string;
  if (!classification || !json[classification]) return settings;

  const value = json[classification];
  if (!value || typeof value !== "string") return settings;
  const match = value.match(/^([^0-9-]*)([-]?\d+(?:,\d{3})*(?:\.\d+)?)(.*)$/);

  if (!match) return settings;

  const [, prefix, number, suffix] = match;
  const cleanNumber = number.replace(/,/g, "");

  return produce(settings, (draft) => {
    draft.number = cleanNumber;
    draft.prefix = prefix;
    draft.suffix = suffix;

    if (reset) delete draft.aiClassification;
  });
};

const updateButton = (
  settings: Record<string, unknown>,
  json: Record<string, unknown>,
  reset = true,
) => {
  return produce(settings, (draft) => {
    const urlClassification = (settings.urlClassification as string) || "";
    const titleClassification = (settings.titleClassification as string) || "";
    const titleClassificationSecondary =
      (settings.titleClassificationSecondary as string) || "";
    const urlClassificationSecondary =
      (settings.urlClassificationSecondary as string) || "";

    draft.url = json[urlClassification] || settings.url;
    draft.title = json[titleClassification] || settings.title;
    draft.titleSecondary =
      json[titleClassificationSecondary] || settings.titleSecondary;
    draft.urlSecondary =
      json[urlClassificationSecondary] || settings.urlSecondary;

    // Remove classification properties
    if (reset) {
      delete draft.urlClassification;
      delete draft.titleClassification;
      delete draft.titleClassificationSecondary;
      delete draft.urlClassificationSecondary;
    }
  });
};

const updateChart = (
  settings: Record<string, unknown>,
  json: Record<string, unknown>,
  reset = true,
) => {
  return produce(settings, (draft) => {
    const data = draft.data as Record<string, unknown>[];

    if (Array.isArray(data)) {
      for (let rowIndex = 0; rowIndex < data.length; rowIndex++) {
        const row = data[rowIndex];

        if (Array.isArray(row)) {
          for (let cellIndex = 0; cellIndex < row.length; cellIndex++) {
            const cell = row[cellIndex] as Record<string, unknown>;

            if (cell && typeof cell === "object") {
              const classification = cell.classification as string | undefined;

              if (classification && typeof classification === "string") {
                const jsonValue = json[classification];

                if (jsonValue) {
                  const isTextOrHeader =
                    classification.startsWith("text") ||
                    rowIndex === 0 ||
                    cellIndex === 0;
                  const isNumeric = !isNaN(parseFloat(jsonValue as string));

                  if (isTextOrHeader) {
                    cell.value = jsonValue;
                  } else if (isNumeric) {
                    cell.value = String(jsonValue);
                  }

                  if (reset) {
                    delete cell.classification;
                  }
                }
              }
            }
          }
        }
      }
    }
  });
};

const updateIcon = (
  settings: Record<string, unknown>,
  json: Record<string, unknown>,
  reset = true,
) => {
  return produce(settings, (draft) => {
    const classification = draft.AIClassificationAnchor as string;

    if (json[classification]) {
      const aiIcon = json[classification] as string;
      let isIconInLibrary: boolean = false;
      if (isIconInLibrary) {
        draft.icon = aiIcon;
      }
    }

    if (!reset && classification) {
      draft.AIClassificationAnchor = classification;
    } else if (reset && draft.AIClassificationAnchor) {
      delete draft.AIClassificationAnchor;
    }
  });
};

const updateSlideBackground = (
  settings: Record<string, unknown>,
  json: Record<string, unknown>,
  reset = true,
) => {
  const AIClassificationAnchor = settings.AIClassificationAnchor as string;
  const value = json[AIClassificationAnchor || ""] as string;

  if (
    !value ||
    settings.backgroundType === "backgroundColor" ||
    !isImageSource(value)
  )
    return settings;

  return produce(settings, (draft) => {
    if (draft.backgroundType) {
      const backgroundType = (draft.backgroundType as string).toLowerCase();
      if (backgroundType === "video") {
        draft.backgroundVideo = value;
      } else if (backgroundType === "image") {
        draft.backgroundImage = value;
      }

      if (!reset && AIClassificationAnchor) {
        draft.AIClassificationAnchor = AIClassificationAnchor;
      } else if (reset && draft.AIClassificationAnchor) {
        delete draft.AIClassificationAnchor;
      }
    }
  });
};

export const updateSingleTabs = (
  settings: Record<string, unknown>,
  json: Record<string, unknown>,
  reset = true,
) => {
  const classification = settings.AIClassificationAnchor as string;

  return produce(settings, (draft) => {
    const name =
      classification && json[classification]
        ? json[classification]
        : settings.tabTitle;

    draft.tabTitle = name;

    if (reset && draft.AIClassificationAnchor) {
      delete draft.AIClassificationAnchor;
    }
  });
};

// ============ Helper Functions ============
